"use client";

import React, { useEffect, useState } from 'react';
import { getMarketingIds, initializeMarketingIds, type MarketingIds } from '@/utils/marketingIds';

/**
 * React hook for managing marketing IDs in forms
 * 
 * This hook provides:
 * - Automatic initialization of marketing ID capture
 * - Current marketing ID values from cookies
 * - Helper function to populate form fields
 */
export const useMarketingIds = () => {
  const [marketingIds, setMarketingIds] = useState<MarketingIds>({});

  useEffect(() => {
    // Initialize marketing ID capture on first render
    initializeMarketingIds();
    
    // Get current marketing IDs
    const ids = getMarketingIds();
    setMarketingIds(ids);
  }, []);

  /**
   * Populate hidden form fields with marketing IDs
   * Call this in your form component to automatically set hidden field values
   */
  const populateFormFields = (formElement: HTMLFormElement): void => {
    Object.entries(marketingIds).forEach(([key, value]) => {
      if (value) {
        // Try to find the field by name or id
        let field = formElement.querySelector(`[name="${key}"]`) as HTMLInputElement;
        if (!field) {
          field = formElement.querySelector(`[id="${key}"]`) as HTMLInputElement;
        }
        
        if (field) {
          field.value = value;
        }
      }
    });
  };

  /**
   * Get hidden field JSX elements for forms
   * Use this to render the required hidden fields in your forms
   */
  const getHiddenFields = (): React.ReactElement[] => {
    const hiddenFields = Object.entries(marketingIds).map(([key, value]) => {
      return React.createElement('input', {
        key: key,
        type: 'hidden',
        name: key,
        id: key,
        value: value || ''
      });
    });
    
    return hiddenFields;
  };

  return {
    marketingIds,
    populateFormFields,
    getHiddenFields,
    refreshMarketingIds: () => setMarketingIds(getMarketingIds())
  };
};
